import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Download } from "lucide-react"
import { 
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid
} from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { format } from "date-fns"
import { InfoTooltip } from "../shared/InfoTooltip"
import { ExportDialog, ExportButton, type ExportColumn } from "../shared/ExportDialog"
import { CHART_CONFIG } from "../../utils/constants"
import { exportToCSV } from "../../utils/formatting"
import type { RawContact } from "../types"
import type { OverviewAnalytics, DailyTrend } from "../../hooks/useAnalytics"

interface UsersTabProps {
  contacts: RawContact[]
  filteredContacts: RawContact[]
  overviewAnalytics: OverviewAnalytics
  dailyTrends: DailyTrend[]
}

export function UsersTab({ 
  contacts, 
  filteredContacts, 
  overviewAnalytics, 
  dailyTrends 
}: UsersTabProps) {
  const [showCumulative, setShowCumulative] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)

  const { userColumns, userData } = useMemo(() => {
    const varKeys = new Set<string>()
    filteredContacts.forEach(c => {
      if (c.vars) Object.keys(c.vars).forEach(k => varKeys.add(k))
    })

    const columns: ExportColumn[] = [
      { key: 'id', label: 'Contact ID' },
      { key: 'phone', label: 'Phone Number' },
      { key: 'name', label: 'Name' },
      { key: 'created', label: 'Created At' },
      { key: 'lastMessage', label: 'Last Message' },
      { key: 'messageCount', label: 'Message Count' },
      { key: 'incomingCount', label: 'Incoming Messages', defaultSelected: false },
      { key: 'outgoingCount', label: 'Outgoing Messages', defaultSelected: false },
      { key: 'status', label: 'Conversation Status' },
      { key: 'handledBy', label: 'Handled By' },
      ...Array.from(varKeys).sort().map(k => ({
        key: `var_${k}`,
        label: `var: ${k}`,
        defaultSelected: ['handled_by', 'assigned_to_name', 'custom_uuid'].includes(k),
      })),
    ]

    const data = filteredContacts.map(c => {
      const row: Record<string, any> = {
        id: c.id,
        phone: c.phone_number || '',
        name: c.name || '',
        created: format(new Date(c.time_created * 1000), 'yyyy-MM-dd HH:mm:ss'),
        lastMessage: c.last_message_time ? format(new Date(c.last_message_time * 1000), 'yyyy-MM-dd HH:mm:ss') : '',
        messageCount: c.message_count || 0,
        incomingCount: c.incoming_message_count || 0,
        outgoingCount: c.outgoing_message_count || 0,
        status: c.conversation_status || '',
        handledBy: c.vars?.handled_by || 'bot',
      }
      if (c.vars) {
        for (const [k, v] of Object.entries(c.vars)) {
          row[`var_${k}`] = v != null ? String(v) : ''
        }
      }
      return row
    })

    return { userColumns: columns, userData: data }
  }, [filteredContacts])

  return (
    <div className="space-y-6">
      {/* User Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Total Users
              <InfoTooltip text="Total unique contacts in the selected date range" />
            </div>
            <div className="text-3xl font-bold">{filteredContacts.length}</div>
            <div className="text-xs text-muted-foreground mt-1">All time: {contacts.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Avg Msgs/User
              <InfoTooltip text="Average number of messages per user in this period" />
            </div>
            <div className="text-3xl font-bold text-purple-600">{overviewAnalytics.avgMessagesPerUser}</div>
          </CardContent>
        </Card>
      </div>

      {/* User Activity Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {showCumulative ? "Cumulative User Growth" : "Daily Active Users"}
              <InfoTooltip text={showCumulative 
                ? "Running total of all users who have ever been active" 
                : "Number of unique users with message activity each day"} 
              />
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor="user-cumulative" className="text-xs text-muted-foreground">Daily</Label>
              <Switch 
                id="user-cumulative" 
                checked={showCumulative} 
                onCheckedChange={setShowCumulative}
              />
              <Label htmlFor="user-cumulative" className="text-xs text-muted-foreground">Cumulative</Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ChartContainer config={CHART_CONFIG} className="h-[300px] w-full">
            {showCumulative ? (
              <AreaChart data={dailyTrends}>
                <defs>
                  <linearGradient id="colorCumulativeUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area type="monotone" dataKey="cumulativeUsers" stroke="#ec4899" fillOpacity={1} fill="url(#colorCumulativeUsers)" name="Total Users" />
              </AreaChart>
            ) : (
              <BarChart data={dailyTrends}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="activeUsers" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Active Users" />
              </BarChart>
            )}
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Export Users */}
      <div className="flex justify-end">
        <ExportButton onClick={() => setExportOpen(true)} label="Export Users" />
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        title="Export Users"
        description={`Export ${filteredContacts.length} contacts as CSV — choose which columns to include`}
        columns={userColumns}
        data={userData}
        filename="telerivet_users"
      />
    </div>
  )
}
